import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { EOnboardingTaskType } from '@feathr/blackbox';
import { type IModalStep, openMultiStepModal, showError, toast } from '@feathr/components';
import {
  BillingFields,
  ProjectInfoFields,
  useProjectForm,
} from '@feathr/extender/App/EventAddPage';
import { logUserEvents } from '@feathr/hooks';

interface ICreateFirstProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: (taskType: EOnboardingTaskType) => void;
}

function CreateFirstProjectModal({
  onClose,
  onComplete,
}: Readonly<ICreateFirstProjectModalProps>): JSX.Element | null {
  const { t } = useTranslation();
  const { event, billable, validationErrors, handleBillingChange } = useProjectForm();

  const handleProjectCreation = useCallback(async (): Promise<void> => {
    try {
      const savedEvent = await event.collection?.add(event);

      if (!savedEvent) {
        throw new Error(t("Couldn't create project. Try again."));
      }
      // Log the event creation
      logUserEvents({ 'Created project': { eventId: savedEvent.id } });

      // Show success message
      toast(t('Created project.'), { type: 'success' });

      // Call the parent's onComplete callback
      onComplete?.(EOnboardingTaskType.CreateFirstProject);
    } catch (error) {
      // Show error message
      showError(error, t);
    }
  }, [event, validationErrors, onComplete, t]);

  const sharedStepProps: Partial<IModalStep> = {
    title: t('Create your first project'),
    size: 'default',
    description: (
      <Trans t={t}>
        Projects are folders where you'll save all campaigns related to a single initiative.&nbsp;
        <a
          href={'https://help.feathr.co/hc/en-us/articles/360047614053-What-is-a-Project'}
          target={'_blank'}
        >
          What is a project?
        </a>
      </Trans>
    ),
  };

  useEffect(() => {
    const steps: IModalStep[] = [
      {
        ...sharedStepProps,
        content: <ProjectInfoFields event={event} />,
        errors: validationErrors,
        nextStepDisabled: !event.isValid(),
      },
      {
        ...sharedStepProps,
        content: (
          <BillingFields billable={billable} event={event} onBillingChange={handleBillingChange} />
        ),
        completeButtonText: t('Create'),
      },
    ];

    openMultiStepModal(steps, handleProjectCreation, onClose);
  }, [onClose, handleProjectCreation, event.isValid(), t]);

  return null;
}

export default observer(CreateFirstProjectModal);
